import { Component, Inject, computed, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { startWith } from 'rxjs';
import { CourseInstance, Student } from '../../core/models/domain.models';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { LocalizationService } from '../../core/i18n/localization.service';
import { EnterSubmitDirective } from '../../core/directives/enter-submit.directive';
import { PL_DASH_DATE_FORMATS, PlDateAdapter } from '../../core/utils/pl-date-adapter';

export interface AddPaymentDialogData {
  students: Student[];
  courseInstances: CourseInstance[];
  addedByDisplay: string;
  addedByUserId: number;
  fixedStudentId?: string | number;
}

@Component({
  selector: 'app-add-payment-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    EnterSubmitDirective,
    TranslatePipe
  ],
  providers: [
    { provide: DateAdapter, useClass: PlDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PL_DASH_DATE_FORMATS }
  ],
  template: `
    <h2 mat-dialog-title>{{ 'paymentDialog.title' | t }}</h2>
    <form appEnterSubmit [formGroup]="form" (ngSubmit)="submit()">
    <mat-dialog-content>
      <div class="payment-form">
        @if (isStudentFixed()) {
          <mat-form-field>
            <mat-label>{{ 'paymentDialog.student' | t }}</mat-label>
            <input matInput [value]="fixedStudentLabel()" readonly />
          </mat-form-field>
        } @else {
          <mat-form-field>
            <mat-label>{{ 'paymentDialog.student' | t }}</mat-label>
            <input matInput formControlName="studentQuery" [matAutocomplete]="studentAuto" (blur)="onStudentBlur()" />
            <mat-autocomplete #studentAuto="matAutocomplete" (optionSelected)="onStudentSelected($event.option.value)">
              @for (student of filteredStudents(); track student.id) {
                <mat-option [value]="student.id">{{ student.name }} {{ student.surname }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        }

        <mat-form-field>
          <mat-label>{{ 'paymentDialog.courseInstance' | t }}</mat-label>
          <mat-select formControlName="courseInstanceId">
            @for (course of availableCoursesForSelectedStudent(); track course.id) {
              <mat-option [value]="course.id">{{ courseLabel(course) }}</mat-option>
            }
          </mat-select>
          @if (form.controls.studentId.value && availableCoursesForSelectedStudent().length === 0) {
            <mat-hint class="missing-relation-hint">{{ 'paymentDialog.noAssignedCourseHint' | t }}</mat-hint>
          }
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ 'paymentDialog.amount' | t }}</mat-label>
          <input matInput class="amount-input" type="number" inputmode="decimal" formControlName="amount" />
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ 'paymentDialog.paymentDate' | t }}</mat-label>
          <input matInput [matDatepicker]="paymentDatePicker" formControlName="paymentDate" />
          <mat-datepicker-toggle matIconSuffix [for]="paymentDatePicker"></mat-datepicker-toggle>
          <mat-datepicker #paymentDatePicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ 'paymentDialog.paymentType' | t }}</mat-label>
          <mat-select formControlName="paymentType">
            <mat-option value="cash">{{ 'common.paymentType.cash' | t }}</mat-option>
            <mat-option value="card">{{ 'common.paymentType.card' | t }}</mat-option>
            <mat-option value="transfer">{{ 'common.paymentType.transfer' | t }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ 'paymentDialog.collectedBy' | t }}</mat-label>
          <input matInput formControlName="paidTo" />
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ 'paymentDialog.addedBy' | t }}</mat-label>
          <input matInput [value]="data.addedByDisplay" readonly />
        </mat-form-field>

        <mat-form-field class="full-width">
          <mat-label>{{ 'paymentDialog.comment' | t }}</mat-label>
          <textarea matInput rows="2" formControlName="additionalInformation"></textarea>
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="dialogRef.close(null)">{{ 'common.cancel' | t }}</button>
      <button mat-flat-button type="submit" color="primary" [disabled]="form.invalid || availableCoursesForSelectedStudent().length === 0">{{ 'paymentDialog.addPayment' | t }}</button>
    </mat-dialog-actions>
    </form>
  `,
  styles: `
    form { display: contents; }
    .payment-form {
      display: grid;
      grid-template-columns: repeat(2, minmax(240px, 1fr));
      gap: 12px;
      min-width: 760px;
    }
    .full-width {
      grid-column: 1 / -1;
    }
    .amount-input::-webkit-inner-spin-button,
    .amount-input::-webkit-outer-spin-button {
      margin: 0;
      appearance: none;
    }
    .amount-input {
      appearance: textfield;
      -moz-appearance: textfield;
    }
    .missing-relation-hint { color: #b54708; font-weight: 600; }
    @media (max-width: 760px) {
      .payment-form {
        grid-template-columns: 1fr;
        min-width: 280px;
      }
      .full-width {
        grid-column: auto;
      }
    }
  `
})
export class AddPaymentDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly localization = inject(LocalizationService);

  readonly isStudentFixed = computed(() => this.data.fixedStudentId != null && `${this.data.fixedStudentId}`.trim().length > 0);
  readonly fixedStudentLabel = computed(() => {
    const selected = this.data.students.find((item) => this.sameId(item.id, this.data.fixedStudentId));
    return selected ? `${selected.name} ${selected.surname}` : '';
  });

  readonly form = this.fb.nonNullable.group({
    studentQuery: ['', Validators.required],
    studentId: ['' as string | number, Validators.required],
    courseInstanceId: ['' as string | number, Validators.required],
    amount: [null as number | null, [Validators.required, Validators.min(1)]],
    paymentDate: [new Date(), Validators.required],
    paymentType: ['cash', Validators.required],
    paidTo: ['', Validators.required],
    additionalInformation: ['']
  });

  private readonly studentQuerySignal = toSignal(this.form.controls.studentQuery.valueChanges.pipe(startWith('')), {
    initialValue: ''
  });
  private readonly studentIdSignal = toSignal(this.form.controls.studentId.valueChanges.pipe(startWith('')), {
    initialValue: ''
  });

  readonly filteredStudents = computed(() => {
    const query = this.studentQuerySignal().toLowerCase().trim();
    return this.data.students.filter((item) => `${item.name} ${item.surname}`.toLowerCase().includes(query));
  });

  readonly availableCoursesForSelectedStudent = computed(() => {
    const studentId = this.studentIdSignal();
    const student = this.data.students.find((item) => this.sameId(item.id, studentId));
    if (!student) {
      return [];
    }

    return this.coursesForStudent(student);
  });

  constructor(
    readonly dialogRef: MatDialogRef<AddPaymentDialogComponent, Record<string, unknown> | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: AddPaymentDialogData
  ) {
    this.form.controls.paidTo.setValue(this.data.addedByDisplay);
    this.form.controls.studentId.valueChanges.subscribe(() => this.syncCourseControl());

    if (this.isStudentFixed()) {
      this.onStudentSelected(this.data.fixedStudentId!);
      this.form.controls.studentQuery.disable({ emitEvent: false });
    }
    this.syncCourseControl();
  }

  private syncCourseControl(): void {
    const control = this.form.controls.courseInstanceId;
    const shouldDisable = this.availableCoursesForSelectedStudent().length === 0;
    if (shouldDisable && !control.disabled) control.disable({ emitEvent: false });
    if (!shouldDisable && control.disabled) control.enable({ emitEvent: false });
  }

  onStudentSelected(studentId: string | number): void {
    const student = this.data.students.find((item) => this.sameId(item.id, studentId));
    if (!student) {
      return;
    }
    const courses = this.coursesForStudent(student);
    this.form.patchValue({
      studentId,
      studentQuery: `${student.name} ${student.surname}`,
      courseInstanceId: courses.length === 1 ? courses[0].id : ''
    });
  }

  onStudentBlur(): void {
    if (this.isStudentFixed()) {
      return;
    }
    const query = this.form.controls.studentQuery.value.trim().toLowerCase();
    if (!query) {
      this.form.patchValue({ studentId: '', courseInstanceId: '' });
      return;
    }
    const exact = this.data.students.find((item) => `${item.name} ${item.surname}`.toLowerCase() === query);
    const partial = this.data.students.filter((item) => `${item.name} ${item.surname}`.toLowerCase().includes(query));
    const match = exact ?? (partial.length === 1 ? partial[0] : undefined);
    if (!match) {
      this.form.patchValue({ studentId: '', courseInstanceId: '' });
      return;
    }
    this.onStudentSelected(match.id);
  }

  submit(): void {
    if (this.form.invalid || this.availableCoursesForSelectedStudent().length === 0) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    this.dialogRef.close({
      studentId: value.studentId,
      courseInstanceId: value.courseInstanceId,
      amount: value.amount,
      paymentDate: this.toIsoDate(value.paymentDate),
      paymentType: value.paymentType,
      paidTo: value.paidTo,
      additionalInformation: value.additionalInformation
    });
  }

  private sameId(left: unknown, right: unknown): boolean {
    return `${left ?? ''}` === `${right ?? ''}`;
  }

  private hasId(values: Set<unknown>, id: unknown): boolean {
    return Array.from(values).some((value) => this.sameId(value, id));
  }

  private coursesForStudent(student: Student): CourseInstance[] {
    const studentCourseIds = new Set(student.courseInstanceIds);
    return this.data.courseInstances.filter((item) => {
      const linkedFromStudent = this.hasId(studentCourseIds, item.id);
      const linkedFromCourse = item.studentIds.some((id) => this.sameId(id, student.id));
      return (linkedFromStudent || linkedFromCourse || item.courseType === 'AD_HOC') && item.status === 'active';
    });
  }

  courseLabel(course: CourseInstance): string {
    if (course.courseType !== 'AD_HOC') return course.name;
    const category = course.categories?.[0] ?? course.category ?? '';
    const categoryLabel = category ? this.localization.translate('common.category') + ' ' + category : '';
    return [this.localization.translate('courseType.adHocShort'), categoryLabel].filter(Boolean).join(' · ');
  }

  private toIsoDate(value: Date | null): string {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return '';
    }
    return [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, '0'),
      String(value.getDate()).padStart(2, '0')
    ].join('-');
  }
}
